import { Box, Button, Divider, Typography } from '@coolblue-development/becky';
import React, { useEffect } from 'react';
import { useChatContext } from '../../../../../contexts/ChatContext';
import { fetchContact } from '../../../../../utils/fetchContact';
import { getChatSession } from '../../../../../utils/getChatSession';
import { isToday } from '../../../../../utils/isToday';
import { datadogLogs } from '@datadog/browser-logs';

const PreviousTranscriptButton = (): JSX.Element | null => {
  const { selectedChat, previousChatMessagesByContact, setPreviousChatMessagesByContact } = useChatContext();
  const [hasPreviousTranscripts, setHasPreviousTranscripts] = React.useState(false);
  const [loader, setLoader] = React.useState(false);

  useEffect(() => {
    const checkForPreviousTranscripts = async (): Promise<void> => {
      // TODO: Add util handler for assuring non-stale contact / chat session
      if (!selectedChat) return;

      const contact = fetchContact(selectedChat);
      if (!contact) return;

      const chatSession = await getChatSession(contact);
      if (!chatSession) return;

      const sessionTranscript = await chatSession.getTranscript({});
      const hasNext = !!sessionTranscript?.data?.NextToken;

      setHasPreviousTranscripts(hasNext);
    };

    void checkForPreviousTranscripts();
  }, [selectedChat]);

  const fetchPreviousTranscripts = async (): Promise<void> => {
    if (!selectedChat) return;

    const contact = fetchContact(selectedChat);
    if (!contact) return;

    const chatSession = await getChatSession(contact);
    if (!chatSession) return;

    try {
      setLoader(true);
      const allMessages: connect.ChatEventData[] = [...(previousChatMessagesByContact[selectedChat] || [])];

      let nextToken: string | undefined = undefined;

      const { contactId } = contact;

      do {
        const transcriptResponse: connect.ParticipantServiceResponse<connect.GetTranscriptResult> =
          await chatSession.getTranscript({ nextToken });
        const messages = (transcriptResponse?.data?.Transcript ?? []).map(item => ({
          ...item,
          ContactId: contactId
        }));

        const isNotFromToday = messages.some(msg => !isToday(msg.AbsoluteTime));
        const todaysMessages = messages.filter(msg => isToday(msg.AbsoluteTime) && msg.Content);

        allMessages.push(...todaysMessages);

        nextToken = isNotFromToday ? undefined : transcriptResponse?.data?.NextToken;
      } while (nextToken);

      allMessages.sort((a, b) => {
        return new Date(a.AbsoluteTime).getTime() - new Date(b.AbsoluteTime).getTime();
      });

      setPreviousChatMessagesByContact(prev => ({
        ...prev,
        [selectedChat]: allMessages
      }));

      datadogLogs.logger.info(`Previous Transcript Button clicked`, { previousTranscript: true });
    } catch (error) {
      datadogLogs.logger.error('Error fetching previous transcripts', { error });
    } finally {
      setLoader(false);
    }
  };

  if (!selectedChat) return null;

  const previousChatMessages = previousChatMessagesByContact[selectedChat] || [];

  return (
    <Box data-testid="previous-transcript-container">
      {previousChatMessages.length < 1 && hasPreviousTranscripts && (
        <Button
          data-testid="previous-transcript-button"
          preset="secondary"
          onClick={fetchPreviousTranscripts}
          size="small"
          loading={loader}
          disabled={loader}
        >
          Show Previous Messages
        </Button>
      )}

      {hasPreviousTranscripts && (
        <Divider sx={{ marginY: '16px', marginBottom: '10px' }} data-testid="previous-transcript-divider">
          <Typography sx={{ color: '#666666', padding: '0px 10px' }}>Current conversation</Typography>
        </Divider>
      )}
    </Box>
  );
};

export default PreviousTranscriptButton;
